import { z } from 'zod'

import {
    CHANNELS_WITH_PRICING_VALIDATION,
    CHANNEL_ID,
    DEFAULT_MAX_PRICE_DIFF_RATIO,
} from '~/consts'
import { roundToDecimalPlaces } from '~/utils'
import { type ErrorMessageOption, getErrorMessage } from './error'
import type { ValidationFunction, ValidationResult } from './types'

export type PriceValidator = {
    validate: (price: unknown) => ValidationResult
}

type ValidationBaseOptions<Value> = { errorMessage?: ErrorMessageOption<Value> }

export function required(options?: ValidationBaseOptions<number>): ValidationFunction {
    return (value: number) => {
        return value > 0
            ? { success: true }
            : {
                  success: false,
                  error: getErrorMessage(options?.errorMessage, 'Price is required', value),
              }
    }
}

export function maxPriceInRange(
    prices: number[],
    options?: ValidationBaseOptions<number>
): ValidationFunction {
    const price = Math.max(...prices)
    return (value) => {
        if (value <= price) {
            return { success: true }
        }

        return {
            success: false,
            error: getErrorMessage(options?.errorMessage, `Max price ${price.toFixed(2)}`, price),
        }
    }
}

export function minPriceRatio(
    prices: number[],
    options?: ValidationBaseOptions<number> & { ratio?: number }
): ValidationFunction {
    const ratio = options?.ratio ?? 1 - DEFAULT_MAX_PRICE_DIFF_RATIO
    const minPrice = roundToDecimalPlaces(Math.min(...prices) * ratio)

    return (value) => {
        if (value >= minPrice) {
            return { success: true }
        }

        return {
            success: false,
            error: getErrorMessage(
                options?.errorMessage,
                `Min price ${minPrice.toFixed(2)}`,
                minPrice
            ),
        }
    }
}

export function createPriceValidator(
    validators: ValidationFunction[],
    platformRules: Partial<Record<'shopee' | 'lazada', ValidationFunction[]>> = {}
): PriceValidator {
    Object.entries(platformRules).forEach(([platform, rules]) => {
        if (rules) {
            switch (parseInt(platform)) {
                case CHANNEL_ID.Lazada:
                    validators.push(...rules)
                    break
                case CHANNEL_ID.Shopee:
                    validators.push(...rules)
                    break
                default:
                    break
            }
        }
    })

    return {
        validate: (input: unknown) => {
            const value = z.coerce.number().parse(input)

            for (const validator of validators) {
                const result = validator(value)
                if (!result.success) {
                    return result
                }
            }
            return { success: true }
        },
    }
}

export function shouldValidatePrice(channelId: number): boolean {
    return CHANNELS_WITH_PRICING_VALIDATION.some((id) => id === channelId)
}
